<template>
    <div
        class="l-ss-c-offer-cart"
        :class="{
            'l-ss-c-offer-cart-custom': isCustom,
            'l-ss-c-offer-cart-in-cart': showInCartLabel,
            'l-ss-c-offer-cart-icon-mode': labelAsCartIcon && (!addToCartLabel || customIcon)
        }">
        <app-btn
            v-if="showBtn"
            class="l-ss-c-offer-cart-btn"
            :loading="loadingAddToCart"
            :disabled="initLoading"
            @click.prevent.stop="clickOnAddToCartHandler($event)">
            <span
                v-if="addToCartLabel"
                v-html="customIcon || addToCartLabel"></span>
            <i
                v-else
                v-html="cartIcon"></i>
        </app-btn>

        <div
            class="l-ss-c-offer-cart-inner"
            v-else-if="quantityOffersInCart && isCartCountAvailable">
            <button
                class="l-ss-c-offer-cart-action"
                @click.prevent.stop="clickOnAddToCartHandler($event, 'dec')">
                <i v-html="minusIcon"></i>
            </button>

            <span class="l-ss-c-offer-cart-count">{{ quantityOffersInCart }}</span>

            <button
                class="l-ss-c-offer-cart-action"
                @click.prevent.stop="clickOnAddToCartHandler($event, 'inc')">
                <i v-html="plusIcon"></i>
            </button>

            <button
                class="l-ss-c-offer-cart-btn-link"
                @click.prevent.stop="onGoToCart">
                <i v-html="cartIcon"></i>
            </button>
        </div>
    </div>
</template>

<script>
    import { ref, computed } from '@vue/composition-api';

    import AppBtn from '@/core/components/common/app-btn.vue';

    import useOfferCartController from '@/core/components/offer/offer-cart/use-offer-cart-controller';

    import minusIcon from '@/core/assets/images/icons/minus_icon.svg';
    import plusIcon from '@/core/assets/images/icons/plus_icon.svg';
    import cartIcon from '@/core/assets/images/icons/cart-icon.svg';
    import arrowIcon from '@/core/assets/images/icons/arrow-right-results.svg';

    export default {
        components: { AppBtn },
        props: {
            offer: {
                type: Object,
            },
            settings: {
                type: Object,
            },
            isCartCountAvailable: {
                type: Boolean,
                default: false,
            },
            labelAsCartIcon: {
                type: Boolean,
                default: false,
            },
        },
        setup(props, { emit, parent }) {
            const { offer, settings, isCartCountAvailable, labelAsCartIcon } = props;

            const isCustom = ref(false);

            let customAddToCartLabel = ref('');
            let customAddToCartAction = null;
            let customIcon = null;

            let notAvailable = false;

            if (offer.labels?.some(label => label.id === 'notAvailable') || !offer.available) {
                notAvailable = true;
            }

            if (settings.addToCartConfig) {
                try {
                    const { label, action, icon } = settings.addToCartConfig(offer) || {};
                    if (label) {
                        customAddToCartLabel.value = label;
                        isCustom.value = true;
                    }

                    if (action) {
                        customAddToCartAction = action;
                        isCustom.value = true;
                    }

                    if (isCustom.value && labelAsCartIcon && icon) {
                        customIcon = icon === 'default' ? arrowIcon : icon;
                    }
                } catch (error) {
                    console.error(error);
                }
            }

            const {
                quantityOffersInCart,
                loadingAddToCart,
                initLoading,
                onChangeOfferCount,
                onGoToCart,
            } = useOfferCartController(offer, settings);

            const showInCartLabel = computed(() => {
                if (!customAddToCartLabel.value) {
                    return !isCartCountAvailable && quantityOffersInCart.value;
                }

                return false;
            });

            const addToCartLabel = computed(() => {
                if (labelAsCartIcon && !customAddToCartLabel.value) {
                    return null;
                }

                if (!customAddToCartLabel.value) {
                    return showInCartLabel.value ? parent.$t('results.inCart') : parent.$t('results.toCart');
                }

                return customAddToCartLabel.value;
            });

            const showBtn = computed(() => {
                if (notAvailable) {
                    return !!customAddToCartLabel.value;
                }

                return quantityOffersInCart.value <= 0 || !isCartCountAvailable || customAddToCartLabel.value;
            });

            const emitAddToCart = (event) => {
                emit('add-to-cart-click', event);
            };

            const emitRemoveFromCart = (event) => {
                emit('remove-from-cart-click', event);
            };

            const clickOnAddToCartHandler = (event, actionType) => {
                if (customAddToCartAction) {
                    customAddToCartAction(offer);
                } else {
                    let currentActionType = actionType;

                    if (!currentActionType) {
                        if (!isCartCountAvailable && quantityOffersInCart.value > 0) {
                            currentActionType = 'removeAll';
                        } else if (quantityOffersInCart.value <= 0) {
                            currentActionType = 'add';
                        }
                    }

                    onChangeOfferCount(currentActionType).then((operationResult) => {
                        event.quantity = operationResult.quantity;
                        event.oldQuantity = operationResult.oldQuantity;
                        event.delta = operationResult.delta;

                        if (['add', 'inc'].includes(currentActionType)) {
                            emitAddToCart(event);
                        } else {
                            emitRemoveFromCart(event);
                        }
                    });
                }
            };

            return {
                isCustom,
                minusIcon,
                plusIcon,
                cartIcon,
                customIcon,
                notAvailable,
                quantityOffersInCart,
                loadingAddToCart,
                initLoading,
                addToCartLabel,
                showInCartLabel,
                customAddToCartLabel,
                showBtn,
                clickOnAddToCartHandler,
                onChangeOfferCount,
                onGoToCart,
            };
        },
    };
</script>
